import * as React from 'react';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Title from './Title';
import Paper from '@mui/material/Paper';
import { Component } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import axios from "axios";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import * as XLSX from 'xlsx';

class Products extends Component {

    constructor(props) {
        super(props);
        this.state = {
            products: [],
            loading: true,
            selectedProducts: [],
            // Filter states
            filterAvailable: true,
            filterTrial: false,
            filterAddon: false,
            // Quantities for selected products (key: product.id, value: quantity)
            quantities: {},
            // Custom prices mode
            useCustomPrices: false,
            // Custom prices storage (key: `${productId}-${type}-${term}-${invoicePeriod}`, value: custom price)
            customPrices: {},
            // Snackbar for copy feedback
            showCopySnackbar: false,
            snackbarMessage: '',
            snackbarSeverity: 'success',
        };
        this.tableRef = React.createRef();
    }

    componentDidMount() {
        this.populateProductData();
    }

    LinearIndeterminate() {
        return (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
            </Box>
        );
    }

    // Get current date-time in format YYYY-MM-DD HH:MM
    getCurrentDateTime() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    }

    // Get current date-time in format YYYYMMDDHHmm (for filename)
    getFileNameDateTime() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${year}${month}${day}${hours}${minutes}`;
    }

    // Check if product is Perpetual
    isPerpetualProduct(product) {
        return product.group?.name === "Microsoft Perpetual Software";
    }

    // Filter products based on checkbox selections
    getFilteredProducts() {
        const { products, filterAvailable, filterTrial, filterAddon } = this.state;

        return products.filter(product => {
            // If filterAvailable is NOT checked, exclude products where isAvailableForPurchase === true
            if (!filterAvailable && product.isAvailableForPurchase === true) {
                return false;
            }

            // If filterAvailable IS checked, only show products where isAvailableForPurchase === true
            if (filterAvailable && product.isAvailableForPurchase !== true) {
                return false;
            }

            // If filterTrial is NOT checked, exclude products where isTrial === true
            if (!filterTrial && product.isTrial === true) {
                return false;
            }

            // If filterTrial IS checked, only show products where isTrial === true
            if (filterTrial && product.isTrial !== true) {
                return false;
            }

            // If filterAddon is NOT checked, exclude products where isAddon === true
            if (!filterAddon && product.isAddon === true) {
                return false;
            }

            // If filterAddon IS checked, only show products where isAddon === true
            if (filterAddon && product.isAddon !== true) {
                return false;
            }

            return true;
        }).sort((a, b) => {
            // Sort alphabetically by name
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }

    // Handle quantity change
    handleQuantityChange(productId, value) {
        const quantity = parseInt(value) || 1;
        const validQuantity = Math.max(1, quantity); // Minimum 1

        this.setState(prevState => ({
            quantities: {
                ...prevState.quantities,
                [productId]: validQuantity
            }
        }));
    }

    // Get quantity for a product (default 1)
    getQuantity(productId) {
        return this.state.quantities[productId] || 1;
    }

    // Handle custom price change
    handleCustomPriceChange(productId, term, invoicePeriod, value) {
        const key = term === null ? `${productId}-perpetual` : `${productId}-${term}-${invoicePeriod}`;
        const price = parseFloat(value) || 0;

        this.setState(prevState => ({
            customPrices: {
                ...prevState.customPrices,
                [key]: price
            }
        }));
    }

    // Get custom price for a product/plan combination
    getCustomPrice(productId, term, invoicePeriod) {
        const key = term === null ? `${productId}-perpetual` : `${productId}-${term}-${invoicePeriod}`;
        return this.state.customPrices[key];
    }

    // Get effective purchase price (custom if available and mode enabled, otherwise original)
    getEffectivePurchasePrice(product, term, invoicePeriod) {
        const { useCustomPrices } = this.state;
        const isPerpetual = this.isPerpetualProduct(product);

        if (useCustomPrices) {
            const customPrice = this.getCustomPrice(product.id, term, invoicePeriod);
            if (customPrice !== undefined && customPrice !== null) {
                return customPrice;
            }
        }

        // Return original price
        const prices = product.prices || [];
        if (isPerpetual) {
            const purchasePrice = prices.find(pr => pr.type === "purchase");
            return purchasePrice ? parseFloat(purchasePrice.amount) : 0;
        } else {
            const p = prices.find(pr => pr.type === "purchase" && pr.billingPlan?.term === term && pr.billingPlan?.invoicePeriod === invoicePeriod);
            return p ? parseFloat(p.amount) : 0;
        }
    }

    // Calculate totals for summary
    calculateTotals(products) {
        const totals = {
            'purchase-1-1': 0,
            'erp-1-1': 0,
            'purchase-12-1': 0,
            'erp-12-1': 0,
            'purchase-12-12': 0,
            'erp-12-12': 0,
            'perpetual-purchase': 0,
            'perpetual-erp': 0
        };

        products.forEach(product => {
            const quantity = this.getQuantity(product.id);
            const prices = product.prices || [];

            if (this.isPerpetualProduct(product)) {
                // Perpetual products
                const purchasePrice = this.getEffectivePurchasePrice(product, null, null);
                totals['perpetual-purchase'] += purchasePrice * quantity;

                const erpPrice = prices.find(pr => pr.type === "erp");
                if (erpPrice) totals['perpetual-erp'] += parseFloat(erpPrice.amount) * quantity;
            } else {
                // Subscription products
                // Purchase 1/1 (M/M)
                const p11 = this.getEffectivePurchasePrice(product, 1, 1);
                totals['purchase-1-1'] += p11 * quantity;

                // SRP 1/1 (M/M)
                const erp11 = prices.find(pr => pr.type === "erp" && pr.billingPlan?.term === 1 && pr.billingPlan?.invoicePeriod === 1);
                if (erp11) totals['erp-1-1'] += parseFloat(erp11.amount) * quantity;

                // Purchase 12/1 (R/M)
                const p121 = this.getEffectivePurchasePrice(product, 12, 1);
                totals['purchase-12-1'] += p121 * quantity;

                // SRP 12/1 (R/M)
                const erp121 = prices.find(pr => pr.type === "erp" && pr.billingPlan?.term === 12 && pr.billingPlan?.invoicePeriod === 1);
                if (erp121) totals['erp-12-1'] += parseFloat(erp121.amount) * quantity;

                // Purchase 12/12 (R/R)
                const p1212 = this.getEffectivePurchasePrice(product, 12, 12);
                totals['purchase-12-12'] += p1212 * quantity;

                // SRP 12/12 (R/R)
                const erp1212 = prices.find(pr => pr.type === "erp" && pr.billingPlan?.term === 12 && pr.billingPlan?.invoicePeriod === 12);
                if (erp1212) totals['erp-12-12'] += parseFloat(erp1212.amount) * quantity;
            }
        });

        return totals;
    }

    // Check if any selected product is perpetual
    hasPerpetualProducts(products) {
        return products.some(product => this.isPerpetualProduct(product));
    }

    // Get original purchase price
    getOriginalPurchasePrice(product, term, invoicePeriod) {
        const prices = product.prices || [];
        const isPerpetual = this.isPerpetualProduct(product);

        if (isPerpetual) {
            const purchasePrice = prices.find(pr => pr.type === "purchase");
            return purchasePrice ? parseFloat(purchasePrice.amount) : 0;
        } else {
            const p = prices.find(pr => pr.type === "purchase" && pr.billingPlan?.term === term && pr.billingPlan?.invoicePeriod === invoicePeriod);
            return p ? parseFloat(p.amount) : 0;
        }
    }

    // Generate HTML table for email/copy
    generateHtmlTable(products) {
        const { useCustomPrices } = this.state;
        const hasPerpetual = this.hasPerpetualProducts(products);
        const totals = this.calculateTotals(products);
        const currency = products[0]?.prices?.[0]?.currency || 'EUR';

        // Generate HTML table with multiple tbody sections
        let html = `
            <table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; font-family: Arial, sans-serif; font-size: 12px;">
                <thead>
                    <tr style="background-color: #f0f0f0; font-weight: bold;">
                        <th>Id</th>
                        <th>Name</th>
                        <th>Ilość</th>
                        <th>${useCustomPrices ? 'Custom M/M' : 'Purchase M/M'}</th>
                        <th>SRP M/M</th>
                        <th>${useCustomPrices ? 'Custom R/M' : 'Purchase R/M'}</th>
                        <th>SRP R/M</th>
                        <th>${useCustomPrices ? 'Custom R/R' : 'Purchase R/R'}</th>
                        <th>SRP R/R</th>
                        ${hasPerpetual ? `<th>${useCustomPrices ? 'Custom' : 'Purchase'}</th><th>SRP</th>` : ''}
                    </tr>
                </thead>
                <tbody>
        `;

        // Add product rows
        products.forEach(product => {
            const quantity = this.getQuantity(product.id);
            const isPerpetual = this.isPerpetualProduct(product);
            const prices = product.prices || [];

            html += '<tr>';
            html += `<td>${product.id}</td>`;
            html += `<td>${product.name}</td>`;
            html += `<td style="text-align: center;">${quantity}</td>`;

            if (isPerpetual) {
                // Empty cells for subscription columns
                html += '<td></td><td></td><td></td><td></td><td></td><td></td>';

                // Perpetual prices
                const effectivePrice = this.getEffectivePurchasePrice(product, null, null);
                const totalPrice = effectivePrice * quantity;
                const erpPrice = prices.find(pr => pr.type === "erp");
                const erpTotalPrice = erpPrice ? parseFloat(erpPrice.amount) * quantity : 0;
                const curr = prices[0]?.currency || 'EUR';

                html += `<td style="text-align: right;">${effectivePrice > 0 ? `${totalPrice.toFixed(2)} ${curr}` : ''}</td>`;
                html += `<td style="text-align: right;">${erpPrice ? `${erpTotalPrice.toFixed(2)} ${curr}` : ''}</td>`;
            } else {
                // Subscription prices
                [[1, 1], [12, 1], [12, 12]].forEach(([term, invoicePeriod]) => {
                    const effectivePrice = this.getEffectivePurchasePrice(product, term, invoicePeriod);
                    const totalPrice = effectivePrice * quantity;
                    const erpPrice = prices.find(pr => pr.type === "erp" && pr.billingPlan?.term === term && pr.billingPlan?.invoicePeriod === invoicePeriod);
                    const erpTotalPrice = erpPrice ? parseFloat(erpPrice.amount) * quantity : 0;
                    const curr = prices[0]?.currency || 'EUR';

                    html += `<td style="text-align: right;">${effectivePrice > 0 ? `${totalPrice.toFixed(2)} ${curr}` : ''}</td>`;
                    html += `<td style="text-align: right;">${erpPrice ? `${erpTotalPrice.toFixed(2)} ${curr}` : ''}</td>`;
                });

                // Empty cells for perpetual if exists
                if (hasPerpetual) {
                    html += '<td></td><td></td>';
                }
            }

            html += '</tr>';
        });

        html += '</tbody>';

        // Add summary as separate tbody
        html += `
                <tbody style="background-color: #f5f5f5;">
                    <tr style="font-weight: bold; border-top: 2px solid #000;">
                        <td></td>
                        <td>Podsumowanie</td>
                        <td></td>
                        <td style="text-align: right;">${totals['purchase-1-1'].toFixed(2)} ${currency}</td>
                        <td style="text-align: right;">${totals['erp-1-1'].toFixed(2)} ${currency}</td>
                        <td style="text-align: right;">${totals['purchase-12-1'].toFixed(2)} ${currency}</td>
                        <td style="text-align: right;">${totals['erp-12-1'].toFixed(2)} ${currency}</td>
                        <td style="text-align: right;">${totals['purchase-12-12'].toFixed(2)} ${currency}</td>
                        <td style="text-align: right;">${totals['erp-12-12'].toFixed(2)} ${currency}</td>
                        ${hasPerpetual ? `
                            <td style="text-align: right;">${totals['perpetual-purchase'].toFixed(2)} ${currency}</td>
                            <td style="text-align: right;">${totals['perpetual-erp'].toFixed(2)} ${currency}</td>
                        ` : ''}
                    </tr>
                </tbody>
            </table>
        `;

        return html;
    }

    // Copy table to clipboard with HTML formatting
    async copyTableToClipboard(products) {
        const html = this.generateHtmlTable(products);

        // Copy to clipboard
        try {
            const blob = new Blob([html], { type: 'text/html' });
            const clipboardItem = new ClipboardItem({ 'text/html': blob });
            await navigator.clipboard.write([clipboardItem]);
            this.setState({
                showCopySnackbar: true,
                snackbarMessage: 'Tabela skopiowana do schowka!',
                snackbarSeverity: 'success'
            });
        } catch (err) {
            console.error('Failed to copy table:', err);
            this.setState({
                showCopySnackbar: true,
                snackbarMessage: 'Nie udało się skopiować tabeli!',
                snackbarSeverity: 'error'
            });
        }
    }

    // Export to XLSX
    exportToXLSX(products) {
        const { useCustomPrices } = this.state;
        const hasPerpetual = this.hasPerpetualProducts(products);
        const totals = this.calculateTotals(products);
        const currency = products[0]?.prices?.[0]?.currency || 'EUR';

        // Prepare data for XLSX
        const data = [];

        // Add header row
        const headers = ['Id', 'Name', 'Ilość'];
        headers.push(useCustomPrices ? 'Custom M/M' : 'Purchase M/M');
        headers.push('SRP M/M');
        headers.push(useCustomPrices ? 'Custom R/M' : 'Purchase R/M');
        headers.push('SRP R/M');
        headers.push(useCustomPrices ? 'Custom R/R' : 'Purchase R/R');
        headers.push('SRP R/R');
        if (hasPerpetual) {
            headers.push(useCustomPrices ? 'Custom' : 'Purchase');
            headers.push('SRP');
        }
        data.push(headers);

        // Add product rows
        products.forEach(product => {
            const quantity = this.getQuantity(product.id);
            const isPerpetual = this.isPerpetualProduct(product);
            const prices = product.prices || [];
            const row = [product.id, product.name, quantity];

            if (isPerpetual) {
                // Empty cells for subscription columns
                row.push('', '', '', '', '', '');

                // Perpetual prices
                const effectivePrice = this.getEffectivePurchasePrice(product, null, null);
                const totalPrice = effectivePrice * quantity;
                const erpPrice = prices.find(pr => pr.type === "erp");
                const erpTotalPrice = erpPrice ? parseFloat(erpPrice.amount) * quantity : 0;
                const curr = prices[0]?.currency || 'EUR';

                row.push(effectivePrice > 0 ? `${totalPrice.toFixed(2)} ${curr}` : '');
                row.push(erpPrice ? `${erpTotalPrice.toFixed(2)} ${curr}` : '');
            } else {
                // Subscription prices
                [[1, 1], [12, 1], [12, 12]].forEach(([term, invoicePeriod]) => {
                    const effectivePrice = this.getEffectivePurchasePrice(product, term, invoicePeriod);
                    const totalPrice = effectivePrice * quantity;
                    const erpPrice = prices.find(pr => pr.type === "erp" && pr.billingPlan?.term === term && pr.billingPlan?.invoicePeriod === invoicePeriod);
                    const erpTotalPrice = erpPrice ? parseFloat(erpPrice.amount) * quantity : 0;
                    const curr = prices[0]?.currency || 'EUR';

                    row.push(effectivePrice > 0 ? `${totalPrice.toFixed(2)} ${curr}` : '');
                    row.push(erpPrice ? `${erpTotalPrice.toFixed(2)} ${curr}` : '');
                });

                // Empty cells for perpetual if exists
                if (hasPerpetual) {
                    row.push('', '');
                }
            }

            data.push(row);
        });

        // Add empty row
        data.push([]);

        // Add summary row
        const summaryRow = ['', 'Podsumowanie', ''];
        summaryRow.push(`${totals['purchase-1-1'].toFixed(2)} ${currency}`);
        summaryRow.push(`${totals['erp-1-1'].toFixed(2)} ${currency}`);
        summaryRow.push(`${totals['purchase-12-1'].toFixed(2)} ${currency}`);
        summaryRow.push(`${totals['erp-12-1'].toFixed(2)} ${currency}`);
        summaryRow.push(`${totals['purchase-12-12'].toFixed(2)} ${currency}`);
        summaryRow.push(`${totals['erp-12-12'].toFixed(2)} ${currency}`);
        if (hasPerpetual) {
            summaryRow.push(`${totals['perpetual-purchase'].toFixed(2)} ${currency}`);
            summaryRow.push(`${totals['perpetual-erp'].toFixed(2)} ${currency}`);
        }
        data.push(summaryRow);

        // Create worksheet and workbook
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Produkty');

        // Download file with timestamp
        const fileName = `kalkulacja_cenowa_produktow_${this.getFileNameDateTime()}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        this.setState({
            showCopySnackbar: true,
            snackbarMessage: 'Plik XLSX został pobrany!',
            snackbarSeverity: 'success'
        });
    }

    // Send email
    async sendEmail(products) {
        try {
            const html = this.generateHtmlTable(products);
            const dateTime = this.getCurrentDateTime();
            const subject = `${dateTime} Kalkulacja cenowa produktów`;

            const response = await axios.post('/db/SendPriceCalculation', {
                htmlTable: html,
                subject: subject
            });

            if (response.data.success) {
                this.setState({
                    showCopySnackbar: true,
                    snackbarMessage: `Email wysłany do: ${response.data.recipient}!`,
                    snackbarSeverity: 'success'
                });
            } else {
                this.setState({
                    showCopySnackbar: true,
                    snackbarMessage: 'Nie udało się wysłać emaila.',
                    snackbarSeverity: 'error'
                });
            }
        } catch (error) {
            console.error('Failed to send email:', error);
            const errorMessage = error.response?.data?.message || 'Błąd podczas wysyłania emaila!';
            this.setState({
                showCopySnackbar: true,
                snackbarMessage: errorMessage,
                snackbarSeverity: 'error'
            });
        }
    }

    // render a table for the provided products; title is configurable
    renderProductsTable(products, title = "Produkty") {
        const hasPerpetual = this.hasPerpetualProducts(products);
        const { useCustomPrices } = this.state;

        return (
            <React.Fragment>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '90%', margin: '20px auto 0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Title>{title}</Title>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useCustomPrices}
                                    onChange={(e) => this.setState({ useCustomPrices: e.target.checked })}
                                />
                            }
                            label="Użyj zmodyfikowanych cen"
                        />
                    </Box>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Id</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Ilość</TableCell>
                                <TableCell>{useCustomPrices ? 'Custom M/M' : 'Purchase M/M'}</TableCell>
                                <TableCell>SRP M/M</TableCell>
                                <TableCell>{useCustomPrices ? 'Custom R/M' : 'Purchase R/M'}</TableCell>
                                <TableCell>SRP R/M</TableCell>
                                <TableCell>{useCustomPrices ? 'Custom R/R' : 'Purchase R/R'}</TableCell>
                                <TableCell>SRP R/R</TableCell>
                                {hasPerpetual && (
                                    <>
                                        <TableCell>{useCustomPrices ? 'Custom' : 'Purchase'}</TableCell>
                                        <TableCell>SRP</TableCell>
                                    </>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.map((product) => {
                                const quantity = this.getQuantity(product.id);
                                const isPerpetual = this.isPerpetualProduct(product);

                                return (
                                    <TableRow key={product.id}>
                                        <TableCell>{product.id}</TableCell>
                                        <TableCell>{product.name}</TableCell>
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={quantity}
                                                onChange={(e) => this.handleQuantityChange(product.id, e.target.value)}
                                                inputProps={{ min: 1 }}
                                                sx={{
                                                    width: '80px',
                                                    '& .MuiInputBase-input': {
                                                        padding: '4px 8px',
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        {isPerpetual ? (
                                            <>
                                                {/* Empty cells for subscription columns */}
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                {/* Perpetual prices */}
                                                {(() => {
                                                    const prices = product.prices || [];
                                                    const erpPrice = prices.find(pr => pr.type === "erp");
                                                    const originalPrice = this.getOriginalPurchasePrice(product, null, null);
                                                    const effectivePrice = this.getEffectivePurchasePrice(product, null, null);
                                                    const totalPrice = effectivePrice * quantity;
                                                    const erpPriceValue = erpPrice ? parseFloat(erpPrice.amount) * quantity : 0;
                                                    const currency = prices[0]?.currency || 'EUR';

                                                    return (
                                                        <>
                                                            <TableCell>
                                                                {useCustomPrices ? (
                                                                    <Box>
                                                                        <TextField
                                                                            type="number"
                                                                            size="small"
                                                                            value={this.getCustomPrice(product.id, null, null) ?? originalPrice}
                                                                            onChange={(e) => this.handleCustomPriceChange(product.id, null, null, e.target.value)}
                                                                            inputProps={{ min: 0, step: 0.01 }}
                                                                            sx={{
                                                                                width: '100px',
                                                                                '& .MuiInputBase-input': {
                                                                                    padding: '4px 8px',
                                                                                    fontSize: '0.875rem'
                                                                                }
                                                                            }}
                                                                        />
                                                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                                            AB: {originalPrice.toFixed(2)} {currency}
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                            = {totalPrice.toFixed(2)} {currency}
                                                                        </Typography>
                                                                    </Box>
                                                                ) : (
                                                                    originalPrice > 0 ? `${totalPrice.toFixed(2)} ${currency}` : ''
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {erpPrice ? `${erpPriceValue.toFixed(2)} ${currency}` : ''}
                                                            </TableCell>
                                                        </>
                                                    );
                                                })()}
                                            </>
                                        ) : (
                                            <>
                                                {/* Subscription prices */}
                                                {(() => {
                                                    const renderPriceCell = (term, invoicePeriod, keySuffix) => {
                                                        const prices = product.prices || [];
                                                        const erpPrice = prices.find(pr => pr.type === "erp" && pr.billingPlan?.term === term && pr.billingPlan?.invoicePeriod === invoicePeriod);
                                                        const originalPrice = this.getOriginalPurchasePrice(product, term, invoicePeriod);
                                                        const effectivePrice = this.getEffectivePurchasePrice(product, term, invoicePeriod);
                                                        const totalPrice = effectivePrice * quantity;
                                                        const erpTotalPrice = erpPrice ? parseFloat(erpPrice.amount) * quantity : 0;
                                                        const currency = prices[0]?.currency || 'EUR';

                                                        return (
                                                            <React.Fragment key={keySuffix}>
                                                                <TableCell>
                                                                    {useCustomPrices && originalPrice > 0 ? (
                                                                        <Box>
                                                                            <TextField
                                                                                type="number"
                                                                                size="small"
                                                                                value={this.getCustomPrice(product.id, term, invoicePeriod) ?? originalPrice}
                                                                                onChange={(e) => this.handleCustomPriceChange(product.id, term, invoicePeriod, e.target.value)}
                                                                                inputProps={{ min: 0, step: 0.01 }}
                                                                                sx={{
                                                                                    width: '100px',
                                                                                    '& .MuiInputBase-input': {
                                                                                        padding: '4px 8px',
                                                                                        fontSize: '0.875rem'
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                                                AB: {originalPrice.toFixed(2)} {currency}
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                = {totalPrice.toFixed(2)} {currency}
                                                                            </Typography>
                                                                        </Box>
                                                                    ) : (
                                                                        originalPrice > 0 ? `${totalPrice.toFixed(2)} ${currency}` : ''
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {erpPrice ? `${erpTotalPrice.toFixed(2)} ${currency}` : ''}
                                                                </TableCell>
                                                            </React.Fragment>
                                                        );
                                                    };

                                                    return (
                                                        <>
                                                            {renderPriceCell(1, 1, 'mm')}
                                                            {renderPriceCell(12, 1, 'rm')}
                                                            {renderPriceCell(12, 12, 'rr')}
                                                        </>
                                                    );
                                                })()}
                                                {/* Empty cells for perpetual columns if they exist */}
                                                {hasPerpetual && (
                                                    <>
                                                        <TableCell></TableCell>
                                                        <TableCell></TableCell>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Paper>

                {/* Summary Panel */}
                {products.length > 0 && (
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '90%', margin: '20px auto 0', backgroundColor: '#f5f5f5' }}>
                        <Title>Podsumowanie</Title>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Id</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Ilość</TableCell>
                                    <TableCell align="right"><strong>{useCustomPrices ? 'Custom M/M' : 'Purchase M/M'}</strong></TableCell>
                                    <TableCell align="right"><strong>SRP M/M</strong></TableCell>
                                    <TableCell align="right"><strong>{useCustomPrices ? 'Custom R/M' : 'Purchase R/M'}</strong></TableCell>
                                    <TableCell align="right"><strong>SRP R/M</strong></TableCell>
                                    <TableCell align="right"><strong>{useCustomPrices ? 'Custom R/R' : 'Purchase R/R'}</strong></TableCell>
                                    <TableCell align="right"><strong>SRP R/R</strong></TableCell>
                                    {hasPerpetual && (
                                        <>
                                            <TableCell align="right"><strong>{useCustomPrices ? 'Custom' : 'Purchase'}</strong></TableCell>
                                            <TableCell align="right"><strong>SRP</strong></TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    {(() => {
                                        const totals = this.calculateTotals(products);
                                        const currency = products[0]?.prices?.[0]?.currency || 'EUR';

                                        return (
                                            <>
                                                <TableCell align="right"><strong>{totals['purchase-1-1'].toFixed(2)} {currency}</strong></TableCell>
                                                <TableCell align="right"><strong>{totals['erp-1-1'].toFixed(2)} {currency}</strong></TableCell>
                                                <TableCell align="right"><strong>{totals['purchase-12-1'].toFixed(2)} {currency}</strong></TableCell>
                                                <TableCell align="right"><strong>{totals['erp-12-1'].toFixed(2)} {currency}</strong></TableCell>
                                                <TableCell align="right"><strong>{totals['purchase-12-12'].toFixed(2)} {currency}</strong></TableCell>
                                                <TableCell align="right"><strong>{totals['erp-12-12'].toFixed(2)} {currency}</strong></TableCell>
                                                {hasPerpetual && (
                                                    <>
                                                        <TableCell align="right"><strong>{totals['perpetual-purchase'].toFixed(2)} {currency}</strong></TableCell>
                                                        <TableCell align="right"><strong>{totals['perpetual-erp'].toFixed(2)} {currency}</strong></TableCell>
                                                    </>
                                                )}
                                            </>
                                        );
                                    })()}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Paper>
                )}

                {/* Action Buttons Panel */}
                {products.length > 0 && (
                    <Paper sx={{ p: 2, width: '90%', margin: '20px auto 0' }}>
                        <Stack direction="row" spacing={2} justifyContent="center">
                            <Button
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                                onClick={() => this.copyTableToClipboard(products)}
                            >
                                Kopiuj tabelę
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => this.exportToXLSX(products)}
                                color="success"
                            >
                                Generuj XLSX
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<EmailIcon />}
                                onClick={() => this.sendEmail(products)}
                                color="info"
                            >
                                Wyślij do mnie mailem
                            </Button>
                        </Stack>
                    </Paper>
                )}
            </React.Fragment>
        );
    }

    render() {
        const { loading, selectedProducts, filterAvailable, filterTrial, filterAddon, showCopySnackbar, snackbarMessage, snackbarSeverity } = this.state;

        if (loading) {
            return <Box sx={{ width: '100%' }}><LinearProgress /><p><em>&nbsp;&nbsp;Loading... </em></p></Box>;
        }

        const filteredProducts = this.getFilteredProducts();

        return (
            <>
                <Paper sx={{ p: 2, width: '90%', margin: '20px auto 0' }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ justifyContent: 'space-between' }}>
                        {/* Left side: Autocomplete and Clear button */}
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                            <Autocomplete
                                sx={{ minWidth: 300, flex: 1 }}
                                multiple
                                options={filteredProducts}
                                getOptionLabel={(opt) => {
                                    if (!opt) return '';
                                    const baseName = opt.name ? `${opt.name} (${opt.id})` : `${opt.id}`;
                                    const trialSuffix = opt.isTrial === true ? ' (TRIAL)' : '';
                                    const addonSuffix = opt.isAddon === true ? ' (ADD-ON)' : '';
                                    return `${baseName}${trialSuffix}${addonSuffix}`;
                                }}
                                value={selectedProducts}
                                onChange={(event, value) => this.setState({ selectedProducts: value })}
                                renderInput={(params) => <TextField {...params} label="Szukaj produktów" variant="outlined" />}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                filterSelectedOptions
                            />
                            <Button variant="contained" onClick={() => this.clearSelection()}>Wyczyść</Button>
                        </Stack>

                        {/* Right side: Filters */}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filterAvailable}
                                        onChange={(e) => this.setState({ filterAvailable: e.target.checked })}
                                        size="small"
                                    />
                                }
                                label="Available"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filterTrial}
                                        onChange={(e) => this.setState({ filterTrial: e.target.checked })}
                                        size="small"
                                    />
                                }
                                label="Trial"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filterAddon}
                                        onChange={(e) => this.setState({ filterAddon: e.target.checked })}
                                        size="small"
                                    />
                                }
                                label="Add-on"
                            />
                        </Stack>
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Wybierz produkty z listy powyżej, aby zobaczyć aktualne ceny w AB
                        </Typography>
                    </Box>
                </Paper>

                {/* only render table when at least one product selected */}
                {selectedProducts && selectedProducts.length > 0
                    ? this.renderProductsTable(selectedProducts, selectedProducts.length > 1 ? "Wybrane produkty" : "Wybrany produkt")
                    : null
                }

                {/* Snackbar for notifications */}
                <Snackbar
                    open={showCopySnackbar}
                    autoHideDuration={3000}
                    onClose={() => this.setState({ showCopySnackbar: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => this.setState({ showCopySnackbar: false })}
                        severity={snackbarSeverity}
                        sx={{ width: '100%' }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </>
        );
    }

    async populateProductData() {
        try {
            const response = await axios.get("/ab/getproducts");
            const data = response.data ? response.data : response;
            this.setState({ products: data || [], loading: false });
        } catch (error) {
            if (error?.response?.status === 401) { alert("Your access is not allowed."); }
            console.log(error);
            this.setState({ products: [], loading: false });
        }
    }

    clearSelection() {
        this.setState({ selectedProducts: [], quantities: {}, customPrices: {}, useCustomPrices: false });
    }
}

export default Products;
